import React, { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { User, Mic, BookOpen, MessageSquare, Printer, RefreshCw } from 'lucide-react';

// Silenciar warnings específicos de bibliotecas de terceiros no console
if (typeof window !== 'undefined') {
  const originalWarn = console.warn;
  console.warn = function(...args) {
    if (args[0] && typeof args[0] === 'string' && (
      args[0].includes('Duplicate headers found and renamed')
    )) {
      return;
    }
    originalWarn.apply(console, args);
  };

  const originalError = console.error;
  console.error = function(...args) {
    if (args[0] && typeof args[0] === 'string' && (
      args[0].includes('should be greater than 0')
    )) {
      return;
    }
    originalError.apply(console, args);
  };
}

const SCALE = {
  '1-Discordo Totalmente': 1,
  '2-Discordo': 2,
  '3-Neutro': 3,
  '4-Concordo': 4,
  '5-Concordo Totalmente': 5,
};

const DIMS = {
  prof: ['Q2.1', 'Q2.2', 'Q2.3'],
  pal: ['Q3.1', 'Q3.2', 'Q3.3'],
  caso: ['Q4.1', 'Q4.2'],
  aluno: ['Q5.1', 'Q5.2', 'Q5.3'],
};

const parseDate = (str) => {
  if (!str) return null;
  return new Date(str);
};

const parseCalDate = (str) => {
  if (!str) return null;
  const parts = str.split('/');
  if (parts.length !== 3) return null;
  let year = parts[2];
  if (year.length === 2) year = '20' + year;
  return new Date(`${year}-${parts[1]}-${parts[0]}T12:00:00`);
};

const getScore = (val) => {
  if (!val) return null;
  const trimmed = val.trim();
  if (SCALE[trimmed]) return SCALE[trimmed];
  const num = parseInt(trimmed, 10);
  if (!isNaN(num) && num >= 1 && num <= 5) {
    return num;
  }
  return null;
};

const normalizeProfessorName = (name) => {
  if (!name) return 'Professor N/A';

  let clean = name.trim();

  // Remove parenthetical info like "(confirmada)", "(Introdução)", etc.
  clean = clean.replace(/\s*\(.*?\)\s*/g, ' ');

  // Remove suffixes like " - confirmada..." or " - CANCELADA..."
  clean = clean.split(/\s+[-–—]\s+/)[0];

  // Remove "/ ..."
  clean = clean.split(/\s+\/\s+/)[0];

  clean = clean.trim();

  // Unify spelling variations / aliases
  const aliasMap = {
    'Lucia S. G. Barros': 'Lucia Barros',
    'Vanessa Martins dos Santos': 'Vanessa Santos',
    'Eduardo de Rezende Francisco': 'Eduardo Francisco',
  };

  if (aliasMap[clean]) {
    clean = aliasMap[clean];
  }

  if (clean === '' || clean === '-') {
    return 'Professor N/A';
  }

  return clean;
};

// URL base do Google Apps Script (Web App deployed as "Anyone")
const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyEHNkzQ7FXKCYw745WauQT4Uv295Lvgn1qub_sfhnixiCRbJ7uz9wFMTXQG7SdLr32/exec';

const fetchCSV = async (filename) => {
  // Fonte primária: Google Apps Script (CORS liberado, dados em tempo real)
  const cacheBuster = `&t=${Date.now()}`;
  const scriptUrl = `${APPS_SCRIPT_URL}?file=${encodeURIComponent(filename)}${cacheBuster}`;

  const r = await fetch(scriptUrl, { redirect: 'follow' });
  if (r.ok) {
    const text = await r.text();
    // Verifica se retornou conteúdo CSV válido (não HTML de erro)
    if (text && !text.trim().startsWith('<!DOCTYPE') && !text.trim().startsWith('<html')) {
      return text;
    }
    throw new Error(`Resposta inválida recebida do Apps Script para ${filename}`);
  }
  throw new Error(`Falha ao carregar ${filename} do Apps Script (Status HTTP ${r.status})`);
};

const getColorClass = (v) => {
  if (v === null || isNaN(v)) return 'text-slate-400';
  if (v >= 4.5) return 'text-emerald-600';
  if (v >= 3.9) return 'text-amber-600';
  return 'text-rose-600';
};

const getDotBgClass = (v) => {
  if (v === null || isNaN(v)) return 'bg-slate-300';
  if (v >= 4.5) return 'bg-gradient-to-r from-emerald-400 to-emerald-500';
  if (v >= 3.9) return 'bg-gradient-to-r from-amber-400 to-amber-500';
  return 'bg-gradient-to-r from-rose-400 to-rose-500';
};

const EADashboard = () => {
  const [rawResponses, setRawResponses] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [turmaFilter, setTurmaFilter] = useState('all');
  const [profFilter, setProfFilter] = useState('all');
  const [casoFilter, setCasoFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [respCsv, calCsv] = await Promise.all([
          fetchCSV('responses.csv'),
          fetchCSV('calendar.csv')
        ]);

        const respParsed = Papa.parse(respCsv, { header: true, skipEmptyLines: 'greedy' }).data;
        const calParsed = Papa.parse(calCsv, { header: true, skipEmptyLines: 'greedy' }).data;

        let latestDate = null;
        respParsed.forEach(r => {
          if (r.RecordedDate) {
            const d = new Date(r.RecordedDate);
            if (!isNaN(d.getTime())) {
              if (!latestDate || d > latestDate) {
                latestDate = d;
              }
            }
          }
        });

        setLastUpdated(latestDate);
        setRawResponses(respParsed);
        setCalendarData(calParsed);
        setError(null);
      } catch (err) {
        console.error('Erro ao inicializar o dashboard:', err);
        setError('Não foi possível carregar os dados das avaliações.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const sessionSummaries = useMemo(() => {
    if (!rawResponses.length || !calendarData.length) return [];

    const targetTurmas = ['T1', 'T2', 'T3', 'T4', 'T5'];
    const validSessions = [];

    calendarData.forEach((entry) => {
      const turmaStr = entry.Turma ? entry.Turma.trim() : '';
      const matchedTurmas = targetTurmas.filter((t) => turmaStr.includes(t));
      if (matchedTurmas.length === 0) return;

      const calDate = parseCalDate(entry.Data);
      if (!calDate) return;

      matchedTurmas.forEach((turma) => {
        validSessions.push({
          turma,
          date: calDate,
          displayDate: entry.Data,
          professor: normalizeProfessorName(entry.Professor),
          palestrante: entry.Palestrante && entry.Palestrante !== '-' ? entry.Palestrante : 'N/A',
          caso: entry.Caso && entry.Caso !== '-' ? entry.Caso : 'Caso N/A',
          disciplina: entry.Disciplina || 'EA',
          sessionKey: `${turma}-${calDate.getTime()}`,
          responses: []
        });
      });
    });

    const uniqueSessionsMap = new Map();
    validSessions.forEach(s => {
      if (!uniqueSessionsMap.has(s.sessionKey)) {
        uniqueSessionsMap.set(s.sessionKey, s);
      }
    });
    const sessions = Array.from(uniqueSessionsMap.values());

    rawResponses.forEach((r) => {
      const respTurma = r.Turma_Origem ? r.Turma_Origem.trim() : null;
      if (!respTurma) return;
      
      const respDate = parseDate(r.RecordedDate);
      if (!respDate) return;

      const turmaSessions = sessions.filter(s => s.turma === respTurma);
      if (turmaSessions.length === 0) return;

      let closestSession = null;
      let minAbsDiff = Infinity;

      turmaSessions.forEach(session => {
        const diff = respDate.getTime() - session.date.getTime();
        // Permite respostas de até 2 dias antes ou até 14 dias depois
        const startWindowDiff = -2 * 24 * 60 * 60 * 1000;
        const maxWindowDiff = 14 * 24 * 60 * 60 * 1000;

        if (diff >= startWindowDiff && diff <= maxWindowDiff) {
          const absDiff = Math.abs(diff);
          if (absDiff < minAbsDiff) {
            minAbsDiff = absDiff;
            closestSession = session;
          }
        }
      });

      if (closestSession) {
        closestSession.responses.push(r);
      }
    });

    const summaries = [];
    sessions.forEach(session => {
      if (session.responses.length === 0) return;

      const stats = { prof: [], pal: [], caso: [], aluno: [] };
      session.responses.forEach((r) => {
        DIMS.prof.forEach((q) => {
          const s = getScore(r[q]);
          if (s !== null) stats.prof.push(s);
        });
        DIMS.pal.forEach((q) => {
          const s = getScore(r[q]);
          if (s !== null) stats.pal.push(s);
        });
        DIMS.caso.forEach((q) => {
          const s = getScore(r[q]);
          if (s !== null) stats.caso.push(s);
        });
        DIMS.aluno.forEach((q) => {
          const s = getScore(r[q]);
          if (s !== null) stats.aluno.push(s);
        });
      });

      const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

      summaries.push({
        turma: session.turma,
        date: session.date,
        displayDate: session.displayDate,
        professor: session.professor,
        palestrante: session.palestrante,
        caso: session.caso,
        disciplina: session.disciplina,
        n: session.responses.length,
        scores: {
          prof: mean(stats.prof),
          pal: mean(stats.pal),
          caso: mean(stats.caso),
          aluno: mean(stats.aluno),
        },
      });
    });

    return summaries.sort((a, b) => a.date - b.date);
  }, [rawResponses, calendarData]);

  const professorsList = useMemo(() => {
    const profs = [...new Set(sessionSummaries.map((s) => s.professor))].sort();
    return profs.filter((p) => p && p !== 'N/A' && p !== 'Professor N/A');
  }, [sessionSummaries]);

  const casosList = useMemo(() => {
    const casos = [...new Set(sessionSummaries.map((s) => s.caso))].sort();
    return casos.filter((c) => c && c !== 'N/A' && c !== 'Caso N/A' && c !== '-');
  }, [sessionSummaries]);

  const filteredSummaries = useMemo(() => {
    return sessionSummaries.filter((s) => {
      const matchTurma = turmaFilter === 'all' || s.turma === turmaFilter;
      const matchProf = profFilter === 'all' || s.professor === profFilter;
      const matchCaso = casoFilter === 'all' || s.caso === casoFilter;
      return matchTurma && matchProf && matchCaso;
    });
  }, [sessionSummaries, turmaFilter, profFilter, casoFilter]);

  const metrics = useMemo(() => {
    const meanOfScores = (dim) => {
      const values = filteredSummaries.map((s) => s.scores[dim]).filter((v) => v !== null && !isNaN(v));
      return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
    };

    const avgP = meanOfScores('prof');
    const avgL = meanOfScores('pal');
    const avgC = meanOfScores('caso');
    const total = filteredSummaries.reduce((acc, s) => acc + s.n, 0);

    return {
      avgProf: avgP,
      avgPal: avgL,
      avgCaso: avgC,
      totalResponses: total,
    };
  }, [filteredSummaries]);

  const chartData = useMemo(() => {
    return filteredSummaries.map((s) => ({
      name: `${s.turma} - ${s.professor.split(' ')[0]} (${s.displayDate.substring(0, 5)})`,
      prof: s.scores.prof !== null && !isNaN(s.scores.prof) ? parseFloat(s.scores.prof.toFixed(2)) : null,
      pal: s.scores.pal !== null && !isNaN(s.scores.pal) ? parseFloat(s.scores.pal.toFixed(2)) : null,
      caso: s.scores.caso !== null && !isNaN(s.scores.caso) ? parseFloat(s.scores.caso.toFixed(2)) : null,
    }));
  }, [filteredSummaries]);

  const timeData = useMemo(() => {
    return filteredSummaries.map((s) => {
      const scores = [s.scores.prof, s.scores.pal, s.scores.caso].filter((v) => v !== null && !isNaN(v));
      const geral = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
      return {
        displayDate: s.displayDate,
        geral: geral !== null && !isNaN(geral) ? parseFloat(geral.toFixed(2)) : null,
      };
    });
  }, [filteredSummaries]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-8 rounded-2xl bg-rose-50 p-6 text-center text-rose-800 border border-rose-100 shadow-sm max-w-lg mx-auto">
        <p className="font-bold text-lg mb-2">Erro de Carregamento</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/40 p-4 md:p-8 print:p-0 print:bg-white">
      <style>{`
        @media print {
          .print-hidden {
            display: none !important;
          }
          .print-card {
            border: 1px solid #f1f5f9 !important;
            box-shadow: none !important;
            border-radius: 12px !important;
          }
          body {
            background-color: white !important;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-slate-100 pb-6 print:pb-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 bg-gradient-to-r from-slate-900 via-indigo-950 to-indigo-900 bg-clip-text text-transparent">
              Experience Accelerator (EA) Dashboard
            </h1>
            <p className="text-slate-500 font-medium mt-1">
              Monitoramento da Qualidade e Avaliação de Aulas (Turmas T1 a T5)
            </p>
            {lastUpdated && (
              <p className="text-sm text-indigo-600 font-semibold mt-1">
                Última avaliação recebida em: {lastUpdated.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 print-hidden">
            <div className="relative flex items-center gap-2 group">
              <a
                href="https://script.google.com/macros/s/AKfycbyEHNkzQ7FXKCYw745WauQT4Uv295Lvgn1qub_sfhnixiCRbJ7uz9wFMTXQG7SdLr32/exec"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-semibold py-2 px-4 rounded-xl transition duration-200 flex items-center shadow-sm hover:shadow"
              >
                <RefreshCw className="w-4 h-4 mr-2 animate-hover-spin" />
                Atualizar Dados
              </a>

              <div className="text-slate-400 hover:text-indigo-500 cursor-help transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>

              <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-slate-800 text-white text-xs rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none text-left">
                <strong>Atualização de Dados:</strong> Este botão atualiza apenas as <em>Respostas das Avaliações</em> (via Google Script). <br/><br/>
                Para atualizar o <strong>Calendário</strong>, você deve substituir o arquivo <code>calendar.csv</code> diretamente na pasta correspondente do Google Drive.
              </div>
            </div>
            <button
              type="button"
              onClick={handlePrint}
              className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold py-2 px-4 rounded-xl transition duration-200 flex items-center shadow-sm hover:shadow"
            >
              <Printer className="w-4 h-4 mr-2" />
              Salvar em PDF
            </button>

            <select
              value={profFilter}
              onChange={(e) => setProfFilter(e.target.value)}
              className="bg-white border border-slate-200/80 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm cursor-pointer hover:bg-slate-50 transition"
            >
              <option value="all">Todos os Professores</option>
              {professorsList.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            <select
              value={turmaFilter}
              onChange={(e) => setTurmaFilter(e.target.value)}
              className="bg-white border border-slate-200/80 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm cursor-pointer hover:bg-slate-50 transition"
            >
              <option value="all">Todas as Turmas</option>
              <option value="T1">T1</option>
              <option value="T2">T2</option>
              <option value="T3">T3</option>
              <option value="T4">T4</option>
              <option value="T5">T5</option>
            </select>

            <select
              value={casoFilter}
              onChange={(e) => setCasoFilter(e.target.value)}
              className="bg-white border border-slate-200/80 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm cursor-pointer hover:bg-slate-50 transition"
            >
              <option value="all">Todos os Casos</option>
              {casosList.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </header>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card 1: Professor */}
          <div className="print-card bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs text-slate-400 uppercase tracking-wider font-extrabold block">Média Professor</span>
              <p className="text-[10px] text-slate-400 font-semibold leading-normal">Didática, engajamento e clareza da exposição</p>
              <div className="flex items-baseline gap-2 pt-1">
                <span className="text-3xl font-black text-slate-800 tracking-tight">
                  {metrics.avgProf !== null ? metrics.avgProf.toFixed(2) : '---'}
                </span>
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${getDotBgClass(metrics.avgProf)}`}></span>
              </div>
            </div>
            <div className="p-3.5 rounded-xl bg-violet-50 text-violet-600 flex-shrink-0">
              <User className="w-6 h-6" />
            </div>
          </div>

          {/* Card 2: Palestrante */}
          <div className="print-card bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs text-slate-400 uppercase tracking-wider font-extrabold block">Média Palestrante</span>
              <p className="text-[10px] text-slate-400 font-semibold leading-normal">Domínio do tema e relevância do convidado</p>
              <div className="flex items-baseline gap-2 pt-1">
                <span className="text-3xl font-black text-slate-800 tracking-tight">
                  {metrics.avgPal !== null ? metrics.avgPal.toFixed(2) : '---'}
                </span>
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${getDotBgClass(metrics.avgPal)}`}></span>
              </div>
            </div>
            <div className="p-3.5 rounded-xl bg-orange-50 text-orange-600 flex-shrink-0">
              <Mic className="w-6 h-6" />
            </div>
          </div>

          {/* Card 3: Caso */}
          <div className="print-card bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs text-slate-400 uppercase tracking-wider font-extrabold block">Média Caso</span>
              <p className="text-[10px] text-slate-400 font-semibold leading-normal">Qualidade do estudo e debates gerados</p>
              <div className="flex items-baseline gap-2 pt-1">
                <span className="text-3xl font-black text-slate-800 tracking-tight">
                  {metrics.avgCaso !== null ? metrics.avgCaso.toFixed(2) : '---'}
                </span>
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${getDotBgClass(metrics.avgCaso)}`}></span>
              </div>
            </div>
            <div className="p-3.5 rounded-xl bg-cyan-50 text-cyan-600 flex-shrink-0">
              <BookOpen className="w-6 h-6" />
            </div>
          </div>

          {/* Card 4: Respostas */}
          <div className="print-card bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs text-slate-400 uppercase tracking-wider font-extrabold block">Total Respostas</span>
              <p className="text-[10px] text-slate-400 font-semibold leading-normal">Volume de avaliações enviadas pelos alunos</p>
              <div className="flex items-baseline gap-2 pt-1">
                <span className="text-3xl font-black text-slate-800 tracking-tight">
                  {metrics.totalResponses}
                </span>
              </div>
            </div>
            <div className="p-3.5 rounded-xl bg-slate-100 text-slate-600 flex-shrink-0">
              <MessageSquare className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className={`grid grid-cols-1 ${profFilter !== 'all' || turmaFilter !== 'all' || casoFilter !== 'all' ? 'lg:grid-cols-2' : ''} gap-8`}>
          {/* Chart 1 */}
          <div className="print-card bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col min-w-0">
            <h3 className="text-md font-bold text-slate-800 mb-4 flex items-center gap-2">
              Média por Sessão
            </h3>
            {chartData.length > 0 ? (
              <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height={320} minWidth={0}>
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} tickLine={false} />
                    <YAxis domain={[3, 5]} stroke="#94a3b8" fontSize={11} tickLine={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                      cursor={{ fill: '#f8fafc' }}
                    />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px', color: '#64748b', paddingTop: '10px' }} />
                    <Bar dataKey="prof" name="Professor" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="pal" name="Palestrante" fill="#f97316" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="caso" name="Caso" fill="#0891b2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-80 items-center justify-center text-slate-400 text-sm">Sem dados disponíveis</div>
            )}
          </div>

          {/* Chart 2 */}
          {(profFilter !== 'all' || turmaFilter !== 'all' || casoFilter !== 'all') && (
            <div className="print-card bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col min-w-0">
              <h3 className="text-md font-bold text-slate-800 mb-4 flex items-center gap-2">
                Evolução Temporal
              </h3>
              {timeData.length > 0 ? (
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height={320} minWidth={0}>
                    <AreaChart data={timeData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorGeral" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="displayDate" stroke="#94a3b8" fontSize={11} tickLine={false} />
                      <YAxis domain={[3, 5]} stroke="#94a3b8" fontSize={11} tickLine={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                      />
                      <Area type="monotone" dataKey="geral" name="Média Geral" stroke="#6366f1" strokeWidth={2.5} fillOpacity={1} fill="url(#colorGeral)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex h-80 items-center justify-center text-slate-400 text-sm">Sem dados disponíveis</div>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="print-card bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h3 className="text-md font-bold text-slate-800">
              Detalhamento por Sessão
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="p-4 text-xs font-extrabold text-slate-500 uppercase tracking-wider">Data</th>
                  <th className="p-4 text-xs font-extrabold text-slate-500 uppercase tracking-wider">Turma</th>
                  <th className="p-4 text-xs font-extrabold text-slate-500 uppercase tracking-wider">Professor</th>
                  <th className="p-4 text-xs font-extrabold text-slate-500 uppercase tracking-wider">Caso</th>
                  <th className="p-4 text-xs font-extrabold text-slate-500 uppercase tracking-wider">N</th>
                  <th className="p-4 text-xs font-extrabold text-slate-500 uppercase tracking-wider">Geral</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/70">
                {filteredSummaries.map((s, idx) => {
                  const scoresList = [s.scores.prof, s.scores.pal, s.scores.caso].filter(
                    (v) => v !== null && !isNaN(v)
                  );
                  const geral = scoresList.length ? scoresList.reduce((a, b) => a + b, 0) / scoresList.length : null;
                  return (
                    <tr key={idx} className="hover:bg-slate-50/30 transition duration-150">
                      <td className="p-4 text-sm text-slate-600 font-medium">{s.displayDate}</td>
                      <td className="p-4 text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
                          {s.turma}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-slate-800 font-semibold">{s.professor}</td>
                      <td className="p-4 text-sm text-slate-500 font-medium">{s.caso}</td>
                      <td className="p-4 text-sm text-slate-500 font-semibold">{s.n}</td>
                      <td className={`p-4 text-sm font-black ${getColorClass(geral)}`}>
                        {geral !== null ? geral.toFixed(2) : '---'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="mt-8 text-center pb-4 opacity-60">
          <p className="text-[10px] text-slate-400 font-medium">
            Desenvolvido por Roberto • EA Dashboard v1.2.0 • Iniciado em Mai/2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default EADashboard;
